// Aufräumen nach einem vom Talent abgebrochenen Upload — gemeinsam für
// TTSView, STSView, UploadSummary und den Aufräumen-Button der VersionsBar.
//
// Der Dienst entfernt seit dem Integrations-Review 2026-09-24 nur halbe
// (`pending`) Versionen; eine inzwischen vollständige Version bleibt und er
// antwortet 409 `code: 'version_complete'` (Abbruch kam nach dem letzten
// Netzwerkschritt, oder die Nummer war veraltet). Bisher wurde das nur
// geloggt: die fertige Version fehlte in Sidebar, VersionsBar und Summary,
// bis zum nächsten Reload. Jetzt wird der Versionsstand nachgeladen.

import { ApiError, cancelUploadVersion, fetchVersions } from '@/lib/api';
import { useSessionStore } from '@/stores/session';

export type CancelPayload = Parameters<typeof cancelUploadVersion>[1];

/** `removed`: halbe Version weg. `kept`: Version war schon vollständig
 *  (409 version_complete) — Versionsstand ist nachgeladen. `failed`: sonstiger
 *  Fehler, nur geloggt (best effort, wie bisher). */
export type CancelOutcome = 'removed' | 'kept' | 'failed';

export function isVersionCompleteConflict(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const body = err.body;
  // Ohne lesbaren Body zählt jedes 409 der Route als „schon fertig" — die
  // Route kennt keinen anderen 409.
  if (!body || typeof body !== 'object' || !('code' in body)) return true;
  return (body as { code?: unknown }).code === 'version_complete';
}

/** Versionen der Session frisch vom Dienst holen und wholesale setzen
 *  (KV ist die Wahrheit, wie beim Kaltstart in useVersionsHydration). */
export async function refreshVersions(toolId: string, sessionFolder: string): Promise<boolean> {
  try {
    const versions = await fetchVersions(toolId, sessionFolder);
    // Nur übernehmen, wenn der Store noch dieselbe Session zeigt.
    if (useSessionStore.getState().sessionFolder !== sessionFolder) return false;
    useSessionStore.getState().setVersions(versions);
    return true;
  } catch (err) {
    console.warn('[versions] refresh failed:', err);
    return false;
  }
}

export async function cancelAbortedUpload(
  toolId: string,
  payload: CancelPayload,
  logTag: string,
): Promise<CancelOutcome> {
  try {
    await cancelUploadVersion(toolId, payload);
    return 'removed';
  } catch (err) {
    if (isVersionCompleteConflict(err)) {
      await refreshVersions(toolId, payload.sessionFolder);
      return 'kept';
    }
    console.warn(`[${logTag}] cancel cleanup failed`, err);
    return 'failed';
  }
}
